import { market_info_raw } from '@ui/assets/config/market-info-raw';
import { pairs_v4_raw } from '@ui/assets/config/pairs-v4-raw';
import { trade_pair_listings } from '@ui/assets/config/trade-pair-listings';
import {
  PairResponse,
  PairResponseStatusEnum,
} from '@ui/services/backend/generated';
import { toPair } from '@ui/services/backend/hooks/getPairs/toPair';
import { toPairsV4 } from '@ui/services/contract/hooks/PairsManagerFacet/toPairsV4';
import { toMarketInfo } from '@ui/services/contract/hooks/TradingReaderFacet/useGetMarketInfos/toMarketInfo';
import { ChainId } from '@ui/types/chain';
import { TradePair } from '@ui/types/tradePair';
import { getDisplayFundingFeeRate } from '@ui/utils/trade/getDisplayFundingFeeRate';
import { unifyTradePairs } from '@ui/utils/unifyTradePairs';
import { Address } from 'viem';
import { deserialize } from 'wagmi';

const getDefaultPairInfo = (base: Address) => {
  return {
    pairBase: base,
    longQty: 0n,
    shortQty: 0n,
    lpLongAvgPrice: 0n,
    lpShortAvgPrice: 0n,
    fundingFeeRate: 0n,
    lastLongAccFundingFeePerShare: 0n,
  };
};
const getRemotePairList = (chainId: ChainId) => {
  const data = trade_pair_listings[chainId];
  if (!data) {
    console.warn('No trading pair configured for chain: ' + chainId);

    return [];
  }

  return data;
};
const getPairData = (base: Address, chainId: ChainId) => {
  const data = pairs_v4_raw[chainId].find(
    (v) => v.base.toLowerCase() === base.toLowerCase(),
  );
  if (!data) {
    throw new Error(
      'Pair data not found, is the pair supported in FE? ' + base,
    );
  }

  return toPairsV4(deserializeData(data));
};
const getMarketInfo = (base: Address, chainId: ChainId) => {
  const data = market_info_raw[chainId].find(
    (v) => v.pairBase.toLowerCase() === base.toLowerCase(),
  );

  if (!data) {
    console.warn(
      'MarketInfo data not found, may be due to no trading in the chain ' +
        base,
      'falling back to default config',
    );

    return toMarketInfo(getDefaultPairInfo(base));
  } else {
    return toMarketInfo(deserializeData(data));
  }
};

const getScData = (address: Address, chainId: ChainId) => {
  const pairData = getPairData(address, chainId);
  const marketInfo = getMarketInfo(address, chainId);

  const displayFundingFeeRate = getDisplayFundingFeeRate({
    basicFundingRatePerSecond: marketInfo.fundingFeeRate,
    longHoldingFeeRatePerSecond: pairData.longHoldingFeeRate,
    shortHoldingFeeRatePerSecond: pairData.shortHoldingFeeRate,
  }) as NonNullable<ReturnType<typeof getDisplayFundingFeeRate>>;

  return {
    ...pairData,
    ...marketInfo,
    displayLongFundingFeeRatePerSecond:
      displayFundingFeeRate.displayLongFundingFeeRatePerSecond,
    displayShortFundingFeeRatePerSecond:
      displayFundingFeeRate.displayShortFundingFeeRatePerSecond,
  };
};

const deserializeData = <T>(serialized: unknown) => {
  const stringified = JSON.stringify(serialized);

  // with bigint
  const deserialized = deserialize(stringified);

  return deserialized as T;
};

const buildTradePair = (
  remotePair: PairResponse,
  chainId: ChainId,
): TradePair => {
  return Object.freeze<TradePair>({
    ...toPair(remotePair),
    ...getScData(remotePair.base as Address, chainId),
  });
};

const getRemoteTradePairList = (chainId: ChainId): TradePair[] => {
  const pairList = getRemotePairList(chainId);

  return pairList.map((pair) => {
    return buildTradePair(pair, chainId);
  });
};

const tradePairMemoryCache: Partial<Record<ChainId, readonly TradePair[]>> = {};

/**
 * !important
 * getting statically built data,must only use for initial data rendering
 */
export const getStaticTradePairs = (chainId: ChainId): readonly TradePair[] => {
  const tradePairList = tradePairMemoryCache[chainId];
  if (tradePairList) {
    return tradePairList;
  }
  const newTradePairList = Object.freeze(getRemoteTradePairList(chainId));
  tradePairMemoryCache[chainId] = newTradePairList;

  return newTradePairList;
};

/**
 * Filters the trade pairs to ensure that the final result array has unique baseName values.
 * If there are duplicates, it removes the one where isDegenPair === true.
 *
 * !important
 * getting statically built data,must only use for initial data rendering
 *
 * @param chainId - The chain ID to get the trade pairs for.
 * @returns An array of unique trade pairs with unique baseName values.
 */
export const getUniqueStaticTradePairs = (chainId: ChainId): TradePair[] => {
  // Get the list of trade pairs for the given chain ID
  const tradePairs = getStaticTradePairs(chainId);

  // Return the array of unique trade pairs
  return unifyTradePairs(tradePairs);
};

enum SmartContractPairStatus {
  AVAILABLE,
  REDUCE_ONLY,
  CLOSE,
}

export const filterDisabledTradePairs = (
  tradePairs: TradePair[] | undefined,
): TradePair[] | undefined => {
  return tradePairs?.filter(
    (pair) =>
      !(
        pair.pairStatus === PairResponseStatusEnum.ReduceOnly ||
        pair.status === SmartContractPairStatus.REDUCE_ONLY
      ),
  );
};
